import { useState } from 'react';
import { Plus, X, GripVertical, Globe, Mail, User, Target, Tag, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { Button, Card } from '@centraoneplus/ui';
import type {
  AIRoutingConfig,
  PipelineSelectionRule,
  PipelineSelectionRuleType,
} from '@/types/pipeline';
import { ThemedSelect } from '@components/common/ThemedSelect';

interface RoutingRulesSectionProps {
  config: AIRoutingConfig | undefined;
  onChange: (config: AIRoutingConfig) => void;
}

const defaultRoutingConfig: AIRoutingConfig = {
  selectionRules: [],
  emailDomains: [],
  emailPrefixes: [],
};

const RULE_TYPES: { type: PipelineSelectionRuleType; label: string; icon: typeof Globe; example: string }[] = [
  { type: 'domain', label: 'Email Domain', icon: Globe, example: 'example.com or *.enterprise.com' },
  { type: 'email_prefix', label: 'Email Prefix', icon: Mail, example: 'support, billing, sales' },
  { type: 'sender', label: 'Sender Email', icon: User, example: 'vip@customer.com' },
  { type: 'intent', label: 'Detected Intent', icon: Target, example: 'request_refund, track_order' },
  { type: 'category', label: 'AI Category', icon: Tag, example: 'billing, technical, sales' },
  { type: 'keyword', label: 'Keyword Match', icon: Search, example: 'urgent, refund, cancel' },
];

export default function RoutingRulesSection({ config, onChange }: RoutingRulesSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [newRule, setNewRule] = useState<Partial<PipelineSelectionRule>>({
    type: 'domain',
    pattern: '',
    priority: 10,
    isActive: true,
  });

  const routingConfig = config ?? defaultRoutingConfig;

  const handleAddRule = () => {
    if (!newRule.pattern?.trim()) return;

    const rule: PipelineSelectionRule = {
      id: `rule_${Date.now()}`,
      type: newRule.type as PipelineSelectionRuleType,
      pattern: newRule.pattern.trim(),
      priority: newRule.priority || 10,
      isActive: newRule.isActive ?? true,
      description: newRule.description,
    };

    onChange({
      ...routingConfig,
      selectionRules: [...routingConfig.selectionRules, rule],
    });

    setNewRule({
      type: 'domain',
      pattern: '',
      priority: 10,
      isActive: true,
    });
  };

  const handleRemoveRule = (ruleId: string) => {
    onChange({
      ...routingConfig,
      selectionRules: routingConfig.selectionRules.filter((r) => r.id !== ruleId),
    });
  };

  const handleToggleRule = (ruleId: string) => {
    onChange({
      ...routingConfig,
      selectionRules: routingConfig.selectionRules.map((r) =>
        r.id === ruleId ? { ...r, isActive: !r.isActive } : r
      ),
    });
  };

  const handleUpdatePriority = (ruleId: string, priority: number) => {
    onChange({
      ...routingConfig,
      selectionRules: routingConfig.selectionRules.map((r) =>
        r.id === ruleId ? { ...r, priority } : r
      ),
    });
  };

  const getRuleTypeInfo = (type: PipelineSelectionRuleType) => {
    return RULE_TYPES.find((rt) => rt.type === type) || RULE_TYPES[0];
  };

  const sortedRules = [...routingConfig.selectionRules].sort((a, b) => b.priority - a.priority);

  return (
    <Card className="p-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Globe className="h-5 w-5 text-blue-600" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Pipeline Selection Rules</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Define rules to automatically route emails to this pipeline
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-6 space-y-4">
          {/* Existing Rules */}
          {sortedRules.length > 0 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Active Rules ({sortedRules.length})
              </label>
              <div className="space-y-2">
                {sortedRules.map((rule) => {
                  const typeInfo = getRuleTypeInfo(rule.type);
                  const Icon = typeInfo.icon;

                  return (
                    <div
                      key={rule.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        rule.isActive
                          ? 'bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700'
                          : 'bg-gray-50 border-gray-200 opacity-60 dark:bg-gray-900 dark:border-gray-700'
                      }`}
                    >
                      <GripVertical className="h-4 w-4 text-gray-400 cursor-grab dark:text-gray-500" />

                      <div className="flex items-center gap-2 min-w-[140px]">
                        <Icon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{typeInfo.label}</span>
                      </div>

                      <div className="flex-1">
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                          {rule.pattern}
                        </code>
                      </div>

                      <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-500 dark:text-gray-400">Priority:</label>
                        <input
                          type="number"
                          value={rule.priority}
                          onChange={(e) =>
                            handleUpdatePriority(rule.id!, parseInt(e.target.value) || 0)
                          }
                          min={0}
                          max={100}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                        />
                      </div>

                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={rule.isActive}
                          onChange={() => handleToggleRule(rule.id!)}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600 dark:bg-gray-700"></div>
                      </label>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveRule(rule.id!)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add New Rule */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-3 dark:text-gray-200">
              Add New Rule
            </label>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-3">
                <ThemedSelect
                  value={newRule.type}
                  onChange={(e) =>
                    setNewRule({ ...newRule, type: e.target.value as PipelineSelectionRuleType })
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                >
                  {RULE_TYPES.map((rt) => (
                    <option key={rt.type} value={rt.type}>
                      {rt.label}
                    </option>
                  ))}
                </ThemedSelect>
              </div>

              <div className="col-span-5">
                <input
                  type="text"
                  value={newRule.pattern || ''}
                  onChange={(e) => setNewRule({ ...newRule, pattern: e.target.value })}
                  placeholder={getRuleTypeInfo(newRule.type as PipelineSelectionRuleType).example}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                />
              </div>

              <div className="col-span-2">
                <input
                  type="number"
                  value={newRule.priority || 10}
                  onChange={(e) => setNewRule({ ...newRule, priority: parseInt(e.target.value) || 0 })}
                  min={0}
                  max={100}
                  placeholder="Priority"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                />
              </div>

              <div className="col-span-2">
                <Button
                  variant="outline"
                  onClick={handleAddRule}
                  disabled={!newRule.pattern?.trim()}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-2 dark:text-gray-400">
              Rules are evaluated in priority order (highest first). First matching rule wins.
            </p>
          </div>

          {/* Quick Add: Email Domains */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-200">
              Quick Add: Email Domains
            </label>
            <p className="text-xs text-gray-500 mb-2 dark:text-gray-400">
              Comma-separated list of domains that should route to this pipeline
            </p>
            <input
              type="text"
              value={routingConfig.emailDomains?.join(', ') || ''}
              onChange={(e) =>
                onChange({
                  ...routingConfig,
                  emailDomains: e.target.value
                    .split(',')
                    .map((d) => d.trim())
                    .filter(Boolean),
                })
              }
              placeholder="enterprise.com, partner.org, *.vip-client.com"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            />
          </div>

          {/* Quick Add: Email Prefixes */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-200">
              Quick Add: Email Prefixes
            </label>
            <p className="text-xs text-gray-500 mb-2 dark:text-gray-400">
              Comma-separated list of email prefixes (part before @) that should route to this pipeline
            </p>
            <input
              type="text"
              value={routingConfig.emailPrefixes?.join(', ') || ''}
              onChange={(e) =>
                onChange({
                  ...routingConfig,
                  emailPrefixes: e.target.value
                    .split(',')
                    .map((p) => p.trim())
                    .filter(Boolean),
                })
              }
              placeholder="support, billing, sales, vip"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            />
          </div>

          {/* Help Text */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mt-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">How Pipeline Selection Works</h4>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>
                <strong>Domain Match:</strong> Routes emails from specific domains (e.g., enterprise.com)
              </li>
              <li>
                <strong>Email Prefix:</strong> Routes based on the part before @ (e.g., support@...)
              </li>
              <li>
                <strong>Sender Email:</strong> Routes specific sender addresses
              </li>
              <li>
                <strong>Intent:</strong> Routes based on AI-detected intent (e.g., request_refund)
              </li>
              <li>
                <strong>Category:</strong> Routes based on AI category (e.g., billing, technical)
              </li>
              <li>
                <strong>Keyword:</strong> Routes when email contains specific keywords
              </li>
            </ul>
          </div>
        </div>
      )}
    </Card>
  );
}
